import React, { useEffect, useState } from 'react';
import { alpha, Button, Grid, Tooltip, Typography } from '@mui/material';
import { ButtonConfig, FilterValue, PopupMode, PopupType } from 'components/common/grid/';
import { logWarning, prepareTextDataObject } from 'tools/tools';
import toast from 'app/toast';
import { TFunction } from 'i18next';
import { Popup, PopupSettings } from 'components/common/popup/Popup';
import IconHandler from 'components/common/icon/IconHandler';
import { onButtonClickAction } from './index';
import { useMiNavigation } from 'hooks/useNavigation';

export default function CustomButton({
    config,
    controllerUrl,
    uid,
    setPopupConfig,
    setPopupUrl,
    setPopupType,
    setPopupMode,
    setPopupRedirectAfter,
    t,
    reloadGridData,
    setShowLoader,
    id,
    setPopupComponent,
    setPopupComponentProps,
    filtersValue,
}: {
    config: ButtonConfig;
    controllerUrl: string;
    uid: string;
    setPopupUrl?: (url: string) => void;
    setPopupConfig?: (popupConfig: PopupSettings) => void;
    setPopupType?: (type: PopupType) => void;
    setPopupMode?: (mode: PopupMode) => void;
    setPopupRedirectAfter?: (redirect: boolean) => void;
    t: TFunction;
    reloadGridData?: () => void;
    setShowLoader?: (value: boolean) => void;
    id: string;
    setPopupComponent?: (popupComponent: string) => void;
    setPopupComponentProps?: (componentProps: { [key: string]: string }) => void;
    filtersValue: FilterValue;
}) {
    const navigate = useMiNavigation();
    const startIcon = config.icon ?? null;
    const [showConfirmationPopup, setShowConfirmationPopup] = useState(false);
    const [confirmed, setConfirmed] = useState(false);

    const popupSettings: PopupSettings = {
        title: 'async_confirmation_popup_title',
        textOK: 'async_confirmation_popup_confirm',
    };

    const onConfirm = () => {
        setConfirmed(true);
        setShowConfirmationPopup(false);
    };

    useEffect(() => {
        if (confirmed) {
            onClick();
        }
    }, [confirmed]);

    const onClick = () => {
        try {
            onButtonClickAction(
                config,
                controllerUrl,
                'custom',
                navigate,
                {
                    setPopupUrl,
                    setPopupConfig,
                    setPopupType,
                    setPopupMode,
                    setPopupRedirectAfter,
                    setPopupComponent,
                    setPopupComponentProps,
                },
                undefined,
                setShowLoader,
                reloadGridData,
                config?.confirmation?.text
                    ? {
                          confirmed,
                          showConfirmation: () => {
                              setShowConfirmationPopup(true);
                          },
                          confirmation: config?.confirmation?.text,
                          setConfirmed,
                      }
                    : undefined,
                undefined,
                undefined,
                filtersValue,
            );
        } catch (error: any) {
            logWarning(error.message);
            return;
        }
    };

    const { getProcessedTranslate: getBtnText } = prepareTextDataObject(config.text);
    const { getProcessedTranslate: getTooltipText } = prepareTextDataObject(config.tooltip);
    const tooltipText = config.tooltip ? getTooltipText(t) : '';

    // Greyed, NOT the disabled attribute: the button must stay hoverable so the tooltip renders,
    // and a click surfaces the tooltip text as a toast instead of firing the action (MI-30228).
    const handleClick = () => {
        if (config.disabled) {
            if (tooltipText) {
                toast('info', tooltipText);
            }
            return;
        }
        onClick();
    };

    const button = (
        <Button
            onClick={handleClick}
            key={uid + config.name}
            startIcon={<IconHandler icon={startIcon} />}
            variant={config.variant ? (config.variant as any) : 'outlined'}
            data-test={id}
            color={config.color ? (config.color as any) : 'primary'}
            aria-disabled={config.disabled || undefined}
            sx={
                config.disabled
                    ? (theme) => ({
                          cursor: 'default',
                          color: alpha(theme.palette.text.primary, 0.24),
                          borderColor: alpha(theme.palette.text.primary, 0.24),
                          '& .svg-inline--fa, & .svg-icon-mi': {
                              color: alpha(theme.palette.text.primary, 0.24),
                          },
                          '&:hover, &:active': {
                              backgroundColor: 'transparent',
                              color: alpha(theme.palette.text.primary, 0.24),
                              borderColor: alpha(theme.palette.text.primary, 0.24),
                          },
                      })
                    : undefined
            }
        >
            {getBtnText(t)}
        </Button>
    );

    return (
        <>
            <Grid item key={config.name} sx={{ flexShrink: 0 }}>
                {tooltipText ? (
                    <Tooltip title={tooltipText} placement="bottom">
                        {/* span keeps the tooltip anchored — consistent hover target either state */}
                        <span>{button}</span>
                    </Tooltip>
                ) : (
                    button
                )}
            </Grid>
            {showConfirmationPopup && config?.confirmation?.text && (
                <Popup
                    settings={{ ...popupSettings, ...config.confirmation }}
                    t={t}
                    open={showConfirmationPopup}
                    onHide={() => setShowConfirmationPopup(false)}
                    onConfirm={onConfirm}
                    maxWidth={'popupSm'}
                >
                    <Typography>{t(config.confirmation.text)}</Typography>
                </Popup>
            )}
        </>
    );
}
